import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { AppDispatch } from '..';
import { Ricerchedb_table_interface } from '@bludata/common-interfaces';
import moment from 'moment';
import { getOpenServiceDatas } from '@bludata/utility-functions';
import { RicercheDB_tableinterface_custom } from '../../report/ReportMain';
import { errorDialogDefaultFunction } from '@bludata/utility-functions';
import { BluAsyncDialog } from '@bludata/components';
import { downloadWithRetry } from '@focus.cloud/src/utility_objects/storageDownloadWithRetry';

export interface ReportTreeRootProps {
  id: string;
  name: string;
  favorite?: boolean;
  children?: {
    id: string;
    name: string;
    favorite?: boolean;
    children?: {
      id: string;
      name: string;
      favorite?: boolean;
      children?: {
        id: string;
        name: string;
        favorite: boolean;
        children?: {
          id: string;
          name: string;
          favorite: boolean;
        }[];
      }[];
    }[];
  }[];
}

export interface ReportParamsValue {
  key: string;
  value: string;
}

export interface ReportSendParamsValue {
  index: number;
  value: string;
}

export interface ReportParams {
  default: string;
  extra: string;
  form: string;
  titolo: string;
  values: ReportParamsValue[];
}

export const reportCategorie = {
  TUTTI: 'TUTTI',
  PREFERITI: 'PREFERITI',
  CRONOLOGIA: 'CRONOLOGIA',
};

const convertNDaysInTimeString = (date: Date) => {
  function plural(data, str1, str2) {
    if (data === 1) return str1;
    return str2;
  }
  // questa fuznione, dati n giorni trascorsi, dice quanto tempo è passato in modo appropriato

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = today.getTime() - date.getTime();
  const n = Math.floor(diff / (1000 * 60 * 60 * 24));
  let giornoSettimana = date?.getDay();

  switch (true) {
    case n === 0:
      return 'Oggi';
    case n === 1:
      return 'Ieri';
    case n >= 2 && n <= 7:
      return [
        'Domenica',
        'Lunedi',
        'Martedi',
        'Mercoledi',
        'Giovedi',
        'Venerdi',
        'Sabato',
      ][giornoSettimana];
    case n >= 8 && n <= 31:
      return plural(
        Math.floor(n / 7),
        '1 settimana fa',
        `${Math.floor(n / 7)} settimane fa`,
      );
    case n >= 32 && n <= 365:
      return plural(
        Math.floor(n / 30),
        '1 mese fa',
        `${Math.floor(n / 30)} mesi fa`,
      );
    case n >= 366:
      return plural(
        Math.floor(n / 365),
        '1 anno fa',
        `${Math.floor(n / 365)} anni fa`,
      );
  }
};

export interface ReportSliceState {
  reportTable: RicercheDB_tableinterface_custom[];
  reportLoading: boolean;
  reportError: boolean;
  reportTree: ReportTreeRootProps[];
  loadingHistory: boolean;
  history: Ricerchedb_table_interface[];
  historyLoaded: {
    [key: string]: boolean;
  };
  imageExample: string;
  selectedCategory: string;
  reportParams: ReportParams[];
  sendParams: ReportSendParamsValue[];
  reportFilter: string;
  pdfUrl: string;
  elaborazioneInProgress: boolean;
  sendTipoStampa: number;
  sendOrizzontale: boolean;
  parametriVisualizzaAltreInfo1: boolean;
  indexMagazzinoFilter?: number;
}

const initialState: ReportSliceState = {
  reportTable: [],
  reportLoading: false,
  reportError: false,
  reportTree: [],
  loadingHistory: false,
  history: [],
  historyLoaded: {},
  imageExample: '',
  selectedCategory: reportCategorie.TUTTI,
  reportParams: [],
  //Stati per l'invio dei parametri d'elaborazione
  sendParams: [],
  sendTipoStampa: 1,
  sendOrizzontale: false,
  //----------------------------------------------------------------
  reportFilter: '',
  pdfUrl: '',
  elaborazioneInProgress: false,
  parametriVisualizzaAltreInfo1: false,
  indexMagazzinoFilter: undefined,
};

const ReportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    setReportTable(
      state: ReportSliceState,
      action: PayloadAction<RicercheDB_tableinterface_custom[]>,
    ) {
      state.reportTable = action.payload;

      const treeRoot: ReportTreeRootProps[] = [];

      //La cronologia contiene n alberi "Report", uno per ogni range temporale
      if (state.selectedCategory === reportCategorie.CRONOLOGIA) {
        action.payload.forEach((item, index) => {
          // Se non l'ho mai utilizzato non lo renderizzo
          if (
            item.ultimoUtilizzo &&
            (item.ricerca
              .toLowerCase()
              .includes(state.reportFilter.toLowerCase()) ||
              item.livello
                .toLowerCase()
                .includes(state.reportFilter.toLowerCase()))
          ) {
            const livello = item.livello;
            //Genero il primo livello della cronologia, partendo dalla data d'utilizzo della ricerca ciclata
            const dateLevel = convertNDaysInTimeString(
              new Date(item.ultimoUtilizzo),
            );

            const levelDate = treeRoot.find((leaf) => leaf.name === dateLevel);
            //Se non ho il primo livello di sicuro non ho nemmeno quelli più bassi, quindi pusho tutto
            if (!levelDate) {
              if (livello.includes('|')) {
                let parti = livello.split('|');
                let [level1, level2] = parti;

                treeRoot.push({
                  id: `${dateLevel}`,
                  name: dateLevel,
                  children: [
                    {
                      //Non posso utilizzare solo il livello come id, avendo n rami mi genererebbe dei nodi con lo stesso id
                      id: nanoid(),
                      name: level1,
                      children: [
                        {
                          id: nanoid(),
                          name: level2,
                          children: [
                            {
                              id: `${item.id}`,
                              name: item.ricerca,
                              favorite: item.preferito,
                            },
                          ],
                        },
                      ],
                    },
                  ],
                });
              }
            } //Ho già il livello data creato, verifico se ho il sottolivello
            else {
              if (livello.includes('|')) {
                let parti = livello.split('|');
                let [level1, level2] = parti;

                const levelLeaf1 = levelDate.children.find(
                  (leaf) => leaf.name === level1,
                );
                //Se non ho il primo livello di sicuro non ho nemmeno quelli più bassi, quindi pusho tutto
                if (!levelLeaf1) {
                  levelDate.children.push({
                    id: nanoid(),
                    name: level1,
                    children: [
                      {
                        id: nanoid(),
                        name: level2,
                        children: [
                          {
                            id: `${item.id}`,
                            name: item.ricerca,
                            favorite: item.preferito,
                          },
                        ],
                      },
                    ],
                  });
                } else {
                  //Cerco se ho il secondo livello già creato
                  const levelLeaf2 = levelLeaf1.children.find(
                    (leaf) => leaf.name === level2,
                  );
                  if (!levelLeaf2) {
                    levelLeaf1.children.push({
                      id: nanoid(),
                      name: level2,
                      children: [
                        {
                          id: `${item.id}`,
                          name: item.ricerca,
                          favorite: item.preferito,
                        },
                      ],
                    });
                  } else {
                    levelLeaf2.children?.push({
                      id: `${item.id}`,
                      name: item.ricerca,
                      favorite: item.preferito,
                    });
                  }
                }
              } else {
                const levelLeaf = levelDate.children.find(
                  (leaf) => leaf.name === livello,
                );

                if (livello.indexOf('Bludata') >= 0) return;

                if (!levelLeaf) {
                  levelDate.children.push({
                    id: nanoid(),
                    name: livello,
                    children: [
                      {
                        id: `${item.id}`,
                        name: item.ricerca,
                        favorite: item.preferito,
                      },
                    ],
                  });
                } else {
                  levelLeaf.children?.push({
                    id: `${item.id}`,
                    name: item.ricerca,
                    favorite: item.preferito,
                  });
                }
              }
            }
          }
        });
      } else {
        //Caso base, un solo albero "report"
        action.payload.forEach((item, index) => {
          if (
            ((state.selectedCategory === reportCategorie.PREFERITI &&
              item.preferito) ||
              state.selectedCategory === reportCategorie.TUTTI) &&
            (item.ricerca
              .toLowerCase()
              .includes(state.reportFilter.toLowerCase()) ||
              item.livello
                .toLowerCase()
                .includes(state.reportFilter.toLowerCase()))
          ) {
            const livello = item.livello;

            if (livello.includes('|')) {
              let parti = livello.split('|');
              let [level1, level2] = parti;

              const levelLeaf1 = treeRoot.find((leaf) => leaf.name === level1);
              //Se non ho il primo livello di sicuro non ho nemmeno quelli più bassi, quindi pusho tutto
              if (!levelLeaf1) {
                treeRoot.push({
                  id: `${level1}`,
                  name: level1,
                  children: [
                    {
                      id: nanoid(),
                      name: level2,
                      children: [
                        {
                          id: `${item.id}`,
                          name: item.ricerca,
                          favorite: item.preferito,
                        },
                      ],
                    },
                  ],
                });
              } else {
                //Cerco se ho il secondo livello già creato
                const levelLeaf2 = levelLeaf1.children.find(
                  (leaf) => leaf.name === level2,
                );
                if (!levelLeaf2) {
                  levelLeaf1.children.push({
                    id: nanoid(),
                    name: level2,
                    children: [
                      {
                        id: `${item.id}`,
                        name: item.ricerca,
                        favorite: item.preferito,
                      },
                    ],
                  });
                } else {
                  levelLeaf2.children?.push({
                    id: `${item.id}`,
                    name: item.ricerca,
                    favorite: item.preferito,
                  });
                }
              }
            }
            //Caso un solo livello
            else {
              const levelLeaf = treeRoot.find((leaf) => leaf.name === livello);

              if (livello.indexOf('Bludata') >= 0) return;

              if (!levelLeaf) {
                treeRoot.push({
                  id: nanoid(),
                  name: livello,
                  children: [
                    {
                      id: `${item.id}`,
                      name: item.ricerca,
                      favorite: item.preferito,
                    },
                  ],
                });
              } else {
                levelLeaf.children?.push({
                  id: `${item.id}`,
                  name: item.ricerca,
                  favorite: item.preferito,
                });
              }
            }
          }
        });
      }

      state.reportTree = treeRoot;
    },

    setReportLoading(state: ReportSliceState, action: PayloadAction<boolean>) {
      state.reportLoading = action.payload;
    },

    setLoadingHistory(state: ReportSliceState, action: PayloadAction<boolean>) {
      state.loadingHistory = action.payload;
    },

    setHistory(
      state: ReportSliceState,
      action: PayloadAction<Ricerchedb_table_interface[]>,
    ) {
      state.history = [...state.history, ...action.payload];
    },

    setLoadingError(state: ReportSliceState, action: PayloadAction<boolean>) {
      state.reportError = action.payload;
    },

    setHistoryLoaded(state: ReportSliceState, action: PayloadAction<string>) {
      let obj = {
        ...state.historyLoaded,
      };
      obj[action.payload] = true;
      state.historyLoaded = obj;
    },
    setImageExample(state: ReportSliceState, action: PayloadAction<string>) {
      state.imageExample = action.payload;
    },
    setSelectedCategory(
      state: ReportSliceState,
      action: PayloadAction<string>,
    ) {
      state.selectedCategory = action.payload;
    },
    setSendParams(
      state: ReportSliceState,
      action: PayloadAction<ReportSendParamsValue[]>,
    ) {
      state.sendParams = action.payload;
    },
    setReportParams(
      state: ReportSliceState,
      action: PayloadAction<ReportParams[]>,
    ) {
      state.reportParams = action.payload;
    },
    setReportFilter(state: ReportSliceState, action: PayloadAction<string>) {
      state.reportFilter = action.payload;
    },
    setPdfUrl(state: ReportSliceState, action: PayloadAction<string>) {
      state.pdfUrl = action.payload;
    },
    setElaborazioneInProgress(
      state: ReportSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.elaborazioneInProgress = action.payload;
    },
    setSendTipoStampa(state: ReportSliceState, action: PayloadAction<number>) {
      state.sendTipoStampa = action.payload;
    },
    setSendOrizzontale(
      state: ReportSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.sendOrizzontale = action.payload;
    },
    setParametriVisualizzaAltreInfo1(
      state: ReportSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.parametriVisualizzaAltreInfo1 = action.payload;
    },
    setIndexMagazzinoFilter(
      state: ReportSliceState,
      action: PayloadAction<number | undefined>,
    ) {
      state.indexMagazzinoFilter = action.payload;
    },
  },
});

export const fetchReport = (codiceProfilo: string, cronologia: boolean) => {
  return (dispatch: AppDispatch) => {
    dispatch(setReportLoading(true));
    dispatch(setLoadingError(false));

    const isReportNotWorking = (report: string) => {
      return (
        report === 'Mailing - Clienti Portatori LAC Attivi' ||
        report === 'Mailing - Clienti ex portatori LAC' ||
        report === 'Mailing - Potenziali nuovi portatori LAC'
      );
    };

    axios
      .get(
        cronologia
          ? '/focusapi/ricerche/getcronologia'
          : '/focusapi/ricerche/getricerche',
        {
          params: {
            _dc: new Date().getTime(),
          },
        },
      )
      .then((response) => {
        const reportList: RicercheDB_tableinterface_custom[] =
          response.data.data;
        const success = response.data.success;
        if (success) {
          dispatch(
            setReportTable(
              reportList.filter((reportItem) => {
                if (
                  reportItem.profilo &&
                  (reportItem.profiloNome || reportItem.profiloNome2)
                ) {
                  return (
                    codiceProfilo === reportItem.profiloNome ||
                    codiceProfilo === reportItem.profiloNome2
                  );
                }
                if (isReportNotWorking(reportItem.ricerca)) {
                  return false;
                }
                if (reportItem.nascosta) {
                  return false;
                }
                return true;
              }),
            ),
          );
        } else {
          dispatch(setLoadingError(true));
        }

        dispatch(setReportLoading(false));
      })
      .catch(() => {
        dispatch(setReportLoading(false));
        dispatch(setLoadingError(true));
      });
  };
};

export const setPreferito = (
  id: number,
  preferito: boolean,
  codiceProfilo: string,
) => {
  return (dispatch: AppDispatch) => {
    dispatch(setReportLoading(true));
    dispatch(setLoadingError(false));

    axios
      .post('/focusapi/ricerche/setpreferito', {
        id,
        valore: preferito,
      })
      .then((response) => {
        dispatch(fetchReport(codiceProfilo, false));
      })
      .catch(() => {
        dispatch(setReportLoading(false));
        dispatch(setLoadingError(true));
      });
  };
};

export const getImageExample = (url: string) => {
  return (dispatch: AppDispatch) => {
    axios.get(url, { responseType: 'blob' }).then((response) => {
      const blobData = new Blob([response.data], { type: 'image/jpeg' });
      const imageUrl = URL.createObjectURL(blobData);
      dispatch(setImageExample(imageUrl));
    });
  };
};

export const fetchReportParams = (id: number) => {
  return (dispatch: AppDispatch) => {
    dispatch(setReportLoading(true));
    dispatch(setLoadingError(false));
    dispatch(setParametriVisualizzaAltreInfo1(false));
    dispatch(setIndexMagazzinoFilter(undefined));

    axios
      .get('/focusapi/ricerche/parametri', {
        params: {
          id,
        },
      })
      .then((response) => {
        //L'oggetto di parametri che ricevo da visualizzare (reportParams) è diverso da quello che sarà da mandare al server per l'elaborazione (sendParams)
        //Lo switch successivo serve a preparare il default dell'oggetto da mandare al server
        dispatch(setReportParams(response.data.parametri));
        const newArraySendParams: ReportSendParamsValue[] =
          response.data.parametri.map((obj, index) => {
            if (obj.titolo.includes('¹')) {
              dispatch(setParametriVisualizzaAltreInfo1(true));
            }
            if (
              obj.titolo.includes('Specificare la voce MAGAZZINO') &&
              !obj.titolo.includes('LOGICO')
            ) {
              dispatch(setIndexMagazzinoFilter(index));
            }
            const isTag = obj.extra === 'tag' ? true : false;
            switch (obj.form) {
              case 'checklist': {
                const tmpValue = obj.values
                  .map((item) => {
                    return obj.extra === 'prodotto' ? item.value : item.key;
                  })
                  .join(',');
                return {
                  index,
                  value:
                    obj.default === '(tutti)' || obj.default === '*' || isTag
                      ? '*'
                      : tmpValue,
                };
              }
              case 'data': {
                return { index, value: moment().format('DD/MM/YYYY') };
              }
              case 'combo': {
                //Purtroppo una ricerca del CRM funziona in modo "particolare" e per farla andare devo forzare a monte il valore di default
                if (obj.titolo === 'Attività ciclica o campagna:') {
                  return {
                    index,
                    value: '%',
                  };
                } else {
                  return {
                    index,
                    value: obj.default === '(tutti)' ? '*' : obj.default,
                  };
                }
              }
              case 'combon': {
                return {
                  index,
                  value: '-1',
                };
              }
              default: {
                return { index, value: obj.default };
              }
            }
          });
        dispatch(setSendParams(newArraySendParams));
        dispatch(setReportLoading(false));
      })
      .catch(() => {
        dispatch(setReportLoading(false));
        dispatch(setLoadingError(true));
      });
  };
};

export const startElaboraReport = (
  id: number,
  sendParams: ReportSendParamsValue[],
  useTipoStampa: boolean,
  tipoStampa: number,
  orentamento: boolean,
  tipoExport?: 'pdf' | 'xls',
  reportName?: string,
) => {
  return (dispatch: AppDispatch) => {
    dispatch(setLoadingError(false));
    dispatch(setElaborazioneInProgress(true));

    let objSendParams;
    if (useTipoStampa) {
      objSendParams = {
        parametri: sendParams,
        tipostampa: tipoStampa,
        orizzontale: orentamento,
      };
    } else {
      objSendParams = { parametri: sendParams };
    }

    axios
      .get('/focusapi/ricerche/elaborastart', {
        params: {
          id,
          dati: JSON.stringify(objSendParams),
          _dc: new Date().getTime(),
          tipoexport: tipoExport ? tipoExport : 'pdf',
        },
      })
      .then((response) => {
        dispatch(
          fetchElaboraStatus(
            response.data,
            tipoExport ? tipoExport : 'pdf',
            reportName,
          ),
        );
      })
      .catch(() => {
        dispatch(setElaborazioneInProgress(false));
        dispatch(setLoadingError(true));
      });
  };
};

export const fetchElaboraStatus = (
  idCode: string,
  tipoExport: 'pdf' | 'xls',
  reportName?: string,
) => {
  return (dispatch: AppDispatch) => {
    axios
      .get('/focusapi/ricerche/elaborastatus', {
        params: {
          idCode,
          _dc: new Date().getTime(),
        },
      })
      .then((response) => {
        switch (response.data) {
          case 'ready': {
            const { accessToken, _cd } = getOpenServiceDatas();
            axios
              .get('/focusapi/ricerche/elaborafileurl', {
                params: {
                  idCode,
                  _dc: new Date().getTime(),
                  _cd,
                  code: accessToken.replace(/{|}|-/g, ''),
                  tipoExport,
                },
              })
              .then(async (response) => {
                const responseDownload = await downloadWithRetry(
                  response.data,
                );
                if (tipoExport === 'pdf') {
                  setTimeout(
                    () => dispatch(setPdfUrl(responseDownload.url)),
                    50,
                  );
                } else if (tipoExport === 'xls') {
                  const conferma = await BluAsyncDialog({
                    title: 'Esportazione Excel',
                    message:
                      'Report pronto. Clicca "Scarica" per avviare il download del file Excel.',
                    confirmButton: 'Scarica',
                    cancelButton: 'Annulla',
                    type: 'info',
                  });
                  if (conferma) {
                    const now = new Date();
                    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
                    const fileName = reportName
                      ? `${reportName}_${timestamp}.xlsx`
                      : `ricerca_${timestamp}.xlsx`;
                    // Fetch come blob per forzare il nome del file (bypass cross-origin)
                    fetch(responseDownload.url)
                      .then((res) => res.blob())
                      .then((blob) => {
                        const blobUrl = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.setAttribute('download', fileName);
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        window.URL.revokeObjectURL(blobUrl);
                      });
                  }
                }
              })
              .catch(() => {
                errorDialogDefaultFunction(
                  'Errore durante la generazione del file di report.',
                );
              })
              .finally(() => {
                dispatch(setElaborazioneInProgress(false));
              });

            break;
          }
          case 'fail': {
            dispatch(setElaborazioneInProgress(false));
            dispatch(setLoadingError(true));
            break;
          }
          case '0': {
            setTimeout(
              () =>
                dispatch(fetchElaboraStatus(idCode, tipoExport, reportName)),
              1000,
            );
            break;
          }
        }
      })
      .catch(() => {
        dispatch(setElaborazioneInProgress(false));
        dispatch(setLoadingError(true));
      });
  };
};

export const {
  setReportTable,
  setReportLoading,
  setLoadingError,
  setHistory,
  setHistoryLoaded,
  setLoadingHistory,
  setImageExample,
  setSelectedCategory,
  setReportParams,
  setSendParams,
  setReportFilter,
  setPdfUrl,
  setElaborazioneInProgress,
  setSendTipoStampa,
  setSendOrizzontale,
  setParametriVisualizzaAltreInfo1,
  setIndexMagazzinoFilter,
} = ReportSlice.actions;

export default ReportSlice.reducer;
